import {
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Stack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import {
  InsightsProduct,
  SharedInsightsProduct,
} from "../../graphql/fragments/product";
import { isInsightsProduct } from "../../lib/utils";
import MarketPlaceControls from "./components/MarketplaceControls";
import ProductTypeSelect, {
  ProductOption,
  ProductType,
} from "./components/ProductTypeSelect";
import UploadProductImage from "./components/UploadProductImage";
import InsightsForm from "./InsightsForm";
import SharedProductForm from "./SharedProductForm";

interface ProductContainerProps extends RouteComponentProps {
  product: SharedInsightsProduct | InsightsProduct | null;
}

const getFilterForProduct = (
  product: SharedInsightsProduct | InsightsProduct | null
) => {
  if (!product) {
    return (p: ProductOption) => p.value !== "VEC";
  }
  if (product.proprietaryDataset !== "VENN") {
    return (p: ProductOption) => p.value !== "VENN";
  }

  return null;
};

const ProductContainer = (props: ProductContainerProps) => {
  const { product } = props;
  const searchParams = new URLSearchParams(props.location.search);
  const type = product?.proprietaryDataset || searchParams.get("type") || null;

  const getDefaultProductType = (type: string | null) => {
    const typeMapping = {
      vec: "VEC",
      vendorfocus: "VendorFocus",
      observatoryreport: "ObservatoryReport",
      industryanalysis: "IndustryAnalysis",
    };

    return type ? typeMapping[type.toLowerCase()] : "VENN";
  };
  const [productType, setProductType] = useState<ProductType>(
    getDefaultProductType(type)
  );

  const onProductTypeChange = (type: ProductType) => {
    setProductType(type);

    if (!product) {
      props.history.push({
        search: `?type=${type.toLowerCase()}`,
      });
    }
  };

  const [imageFiles, setImageFiles] = useState<FileList | null>();

  const [marketplaceFlags, setMarketplaceFlags] = useState({
    isActive: props.product?.isActive ?? true,
    isFree: props.product?.isFree ?? false,
    isForSale: props.product?.isForSale ?? true,
  });

  const updateMarketplaceFlag = (key: string, value: boolean) => {
    setMarketplaceFlags({
      ...marketplaceFlags,
      [key]: value,
    });
  };

  const isProductSelectVisible =
    !product?.id || product?.proprietaryDataset !== "VENN";

  return (
    <Grid gap={4} templateColumns="repeat(4, 1fr)">
      <GridItem colSpan={3}>
        {(!product && productType === "VENN") ||
        (product && isInsightsProduct(product)) ? (
          <InsightsForm
            product={product}
            marketplaceFlags={marketplaceFlags}
            productImageFile={imageFiles?.[0] || null}
          />
        ) : (
          <SharedProductForm
            product={product}
            marketplaceFlags={marketplaceFlags}
            productType={productType}
            productImageFile={imageFiles?.[0] || null}
          />
        )}
      </GridItem>
      <GridItem>
        <Stack spacing={3}>
          <UploadProductImage
            product={product}
            setImageFiles={setImageFiles}
            imageFiles={imageFiles || null}
          />
          {isProductSelectVisible ? (
            <FormControl>
              <FormLabel>Product Type</FormLabel>
              <ProductTypeSelect
                filter={getFilterForProduct(product)}
                value={productType}
                onChange={(type) => onProductTypeChange(type)}
              />
            </FormControl>
          ) : null}
          <MarketPlaceControls
            onChange={updateMarketplaceFlag}
            marketplaceFlags={marketplaceFlags}
          />
        </Stack>
      </GridItem>
    </Grid>
  );
};

export default withRouter(ProductContainer);
